import React from 'react';
import { View, Pressable, StyleSheet, Platform } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import Icon from '@/components/Icon';
import Text from '@/components/Typography/Text';
import { MAX_DECK_COLUMNS, DEFAULT_DECK_COLUMN_WIDTH, MIN_DECK_COLUMN_WIDTH, MAX_DECK_COLUMN_WIDTH } from '@/constants/config';
import Tooltip from '@/components/Tooltip';
import * as DropdownMenu from '@/components/DropdownMenu/DropdownMenu';
import DropdownMenuItem from '@/components/DropdownMenu/DropdownMenuItem';

const COLUMN_OPTIONS = [
  {
    id: 'conversation',
    spec: {},
    title: 'Chat column',
    subtitle: 'Link or start a conversation',
    icon: 'message',
  },
  {
    id: 'model',
    spec: { type: 'model', label: 'Model' },
    title: 'Model column',
    subtitle: 'Run a base model',
    icon: 'cube',
  },
  {
    id: 'agent',
    spec: { type: 'agent', label: 'Agent' },
    title: 'Agent column',
    subtitle: 'Use an agent persona',
    icon: 'user-astronaut',
  },
  {
    id: 'tool',
    spec: { type: 'tool', label: 'Tool' },
    title: 'Tool column',
    subtitle: 'One-click tool',
    icon: 'wand-magic-sparkles',
  },
];

/**
 * @param {function(Object=): void} onAddColumn
 * @param {number} [columnWidth] — match active deck column width (clamped to min/max).
 */
export default function DeckAddColumnButton({ onAddColumn = () => {}, columnCount, columnWidth = DEFAULT_DECK_COLUMN_WIDTH }) {
  const { colors } = useTheme();
  const isDisabled = columnCount >= MAX_DECK_COLUMNS;
  const w = Math.min(
    MAX_DECK_COLUMN_WIDTH,
    Math.max(MIN_DECK_COLUMN_WIDTH, Number(columnWidth) || DEFAULT_DECK_COLUMN_WIDTH),
  );

  const triggerInner = (
    <Pressable
      style={({ hovered }) => [
        styles.container,
        {
          height: '100%',
          minHeight: '100%',
          borderColor: hovered && !isDisabled ? colors.border_color_primary : colors.border_color_secondary,
          backgroundColor: hovered && !isDisabled ? colors.solid_color_block : 'transparent',
          opacity: isDisabled ? 0.4 : 1,
        },
      ]}
      disabled={isDisabled}
      accessibilityRole="button"
      accessibilityLabel="Add column"
      accessibilityHint="Choose chat, model, agent, or tool column"
    >
      <View style={styles.inner}>
        <Icon name="plus" size={26} color={colors.text_color_secondary} />
        <Text size="s" weight="semibold" color="secondary" style={styles.label}>
          Add column
        </Text>
        {!isDisabled && (
          <Icon name="chevron-down" size={11} color={colors.text_color_tertiary} />
        )}
      </View>
    </Pressable>
  );

  if (isDisabled) {
    return (
      <Tooltip content={`Maximum ${MAX_DECK_COLUMNS} columns`} position="left">
        <View style={[styles.wrapper, { width: w }]}>{triggerInner}</View>
      </Tooltip>
    );
  }

  return (
    <View style={[styles.wrapper, { width: w }]}>
      <DropdownMenu.Root>
        <DropdownMenu.Trigger>
          <View style={styles.triggerFill}>{triggerInner}</View>
        </DropdownMenu.Trigger>
        <DropdownMenu.Content
          className={Platform.OS === 'web' ? 'DropdownMenuContent' : undefined}
          sideOffset={6}
          align="end"
        >
          {COLUMN_OPTIONS.map((opt) => (
            <DropdownMenuItem
              key={opt.id}
              icon={opt.icon}
              title={opt.title}
              subtitle={opt.subtitle}
              onSelect={() => onAddColumn(opt.spec)}
            />
          ))}
        </DropdownMenu.Content>
      </DropdownMenu.Root>
    </View>
  );
}

const styles = StyleSheet.create({
  wrapper: {
    alignSelf: 'stretch',
    height: '100%',
    minHeight: '100%',
    paddingVertical: 0,
    paddingHorizontal: 0,
  },
  /** Fills tab bar / column strip height so the dashed Pressable can use height 100%. */
  triggerFill: {
    width: '100%',
    height: '100%',
    minHeight: '100%',
    alignSelf: 'stretch',
  },
  container: {
    width: '100%',
    height: '100%',
    minHeight: '100%',
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 1,
    borderStyle: 'dashed',
    borderRadius: 0,
    ...(Platform.OS === 'web' ? { cursor: 'pointer' } : {}),
  },
  inner: {
    alignItems: 'center',
    gap: 10,
    paddingVertical: 16,
    paddingHorizontal: 12,
  },
  label: {
    textAlign: 'center',
    paddingHorizontal: 2,
  },
});
